import type { ActivationStatus, ExplorationVolume } from '../../types/exploration';

/** Coarse UI labels for the volume detail sidebar — not drill/chart labels (`volumeStatusLabel`, `REASON_LABELS`). */

export type VolumeDetailEligibilityBucket = 'supported' | 'pendingSetup' | 'unsupported';

export type VolumeDetailAutoscalingBucket = 'active' | 'inactive' | 'unsupported';

type EligibilityFlags = Pick<ExplorationVolume, 'isDatafiable' | 'isPotentiallyDatafiable'>;

/** Eligibility bucket — matches the fleet manager's eligibility filter (Supported/Pending Setup/Unsupported). */
export function volumeDetailEligibilityBucket(v: EligibilityFlags): VolumeDetailEligibilityBucket {
  if (v.isDatafiable) return 'supported';
  if (v.isPotentiallyDatafiable) return 'pendingSetup';
  return 'unsupported';
}

export function eligibilityDetailLabel(v: EligibilityFlags): string {
  const bucket = volumeDetailEligibilityBucket(v);
  if (bucket === 'supported') return 'Supported';
  if (bucket === 'pendingSetup') return 'Pending Setup';
  return 'Unsupported';
}

export function eligibilityIndicatorClass(v: EligibilityFlags): string {
  const bucket = volumeDetailEligibilityBucket(v);
  if (bucket === 'supported') return 'bg-green';
  return 'bg-gray-400';
}

export function volumeDetailAutoscalingBucket(status: ActivationStatus): VolumeDetailAutoscalingBucket {
  if (status === 'active') return 'active';
  if (status === 'unsupported') return 'unsupported';
  return 'inactive';
}

/** Only two values are surfaced to users: Active/Inactive — eligibility carries the Unsupported case. */
export function autoscalingDetailLabel(status: ActivationStatus): string {
  const bucket = volumeDetailAutoscalingBucket(status);
  return bucket === 'active' ? 'Active' : 'Inactive';
}

export function autoscalingIconClass(
  volume: Pick<ExplorationVolume, 'activationStatus' | 'isDatafiable' | 'isPotentiallyDatafiable'>
): string {
  const bucket = volumeDetailAutoscalingBucket(volume.activationStatus);
  if (bucket === 'active') return 'text-green';
  if (bucket === 'unsupported') return 'text-gray-400';
  return volumeDetailEligibilityBucket(volume) === 'supported'
    ? 'text-[var(--exploration-supported)]'
    : 'text-gray-400';
}

/** Tooltip dot indicator — mirrors `eligibilityIndicatorClass` buckets. */
export function eligibilityTooltipDotClass(v: EligibilityFlags): string {
  const bucket = volumeDetailEligibilityBucket(v);
  if (bucket === 'supported') return 'vt-dot-supported';
  if (bucket === 'pendingSetup') return 'vt-dot-unattached';
  return 'vt-dot-unsupported';
}

/** Tooltip autoscaling icon color — mirrors `autoscalingIconClass` buckets. */
export function autoscalingTooltipIconClass(
  volume: Pick<ExplorationVolume, 'activationStatus' | 'isDatafiable' | 'isPotentiallyDatafiable'>
): string {
  const bucket = volumeDetailAutoscalingBucket(volume.activationStatus);
  if (bucket === 'active') return 'vt-autoscale-active';
  if (bucket === 'unsupported') return 'vt-autoscale-unsupported';
  return volumeDetailEligibilityBucket(volume) === 'supported'
    ? 'vt-autoscale-inactive-supported'
    : 'vt-autoscale-unsupported';
}
